import React, { useState } from "react";
import { X, CheckCircle2, Circle, Pencil, Save, XCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useModal } from "../context/ModalContext";
import toast from "react-hot-toast";

// Inline edit form component
const EditTestCaseForm = ({ testCaseId, initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    "Test Scenario": initialData["Test Scenario"] || "",
    Precondition: initialData["Precondition"] || "",
    "Expected Result": initialData["Expected Result"] || "",
    "Test Steps": initialData["Test Steps"] || "",
    Priority: initialData["Priority"] || "medium",
    Status: initialData["Status"] || "active",
    "Test Case ID": initialData["Test Case ID"] || testCaseId,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSave(formData);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCancel();
  };

  return (
    <div
      className="mt-4 p-4 bg-bg-primary border border-indigo-200 rounded-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="test-scenario" className="text-xs mb-1">
            Test Scenario / Title
          </Label>
          <Input
            id="test-scenario"
            type="text"
            value={formData["Test Scenario"]}
            onChange={(e) => handleInputChange("Test Scenario", e.target.value)}
            className="text-sm"
            placeholder="Enter test scenario"
          />
        </div>

        <div>
          <Label htmlFor="precondition" className="text-xs mb-1">
            Precondition
          </Label>
          <textarea
            id="precondition"
            value={formData["Precondition"]}
            onChange={(e) => handleInputChange("Precondition", e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            placeholder="Enter preconditions"
          />
        </div>

        <div>
          <Label htmlFor="expected-result" className="text-xs mb-1">
            Expected Result
          </Label>
          <textarea
            id="expected-result"
            value={formData["Expected Result"]}
            onChange={(e) =>
              handleInputChange("Expected Result", e.target.value)
            }
            rows={3}
            className="w-full px-3 py-2 text-sm bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            placeholder="Enter expected result"
          />
        </div>

        <div>
          <Label htmlFor="test-steps" className="text-xs mb-1">
            Test Steps
          </Label>
          <textarea
            id="test-steps"
            value={formData["Test Steps"]}
            onChange={(e) => handleInputChange("Test Steps", e.target.value)}
            rows={4}
            className="w-full px-3 py-2 text-sm bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono resize-none"
            placeholder="Enter test steps (one per line)"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="priority" className="text-xs mb-1">
              Priority
            </Label>
            <Select
              value={formData["Priority"]}
              onValueChange={(value) => handleInputChange("Priority", value)}
            >
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="status" className="text-xs mb-1">
              Status
            </Label>
            <Select
              value={formData["Status"]}
              onValueChange={(value) => handleInputChange("Status", value)}
            >
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2">
          <Button type="submit" size="sm" className="flex-1">
            <Save className="w-4 h-4 mr-1" />
            Save
          </Button>
          <Button
            type="button"
            onClick={handleCancel}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <XCircle className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

const TestCasesPreviewModal = ({
  isOpen,
  onClose,
  testCases,
  onSave,
  onApprove,
  onReject,
  onConfirm,
  projectId,
  sectionId,
  reviewerId,
}) => {
  const { incrementModalCount, decrementModalCount } = useModal();
  const [editingId, setEditingId] = useState(null);
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [rejectedIds, setRejectedIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Register/unregister modal when isOpen changes
  React.useEffect(() => {
    if (isOpen) {
      incrementModalCount();
      return () => {
        decrementModalCount();
      };
    }
  }, [isOpen, incrementModalCount, decrementModalCount]);

  if (!isOpen) return null;

  const handleEdit = (testCaseId) => {
    setEditingId(testCaseId);
  };

  const handleSave = (testCaseId, updatedData) => {
    onSave(testCaseId, updatedData);
    setEditingId(null);
    toast.success("Test case updated successfully");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleToggleApproval = (testCaseId) => {
    const newApproved = new Set(approvedIds);
    const newRejected = new Set(rejectedIds);

    if (newApproved.has(testCaseId)) {
      newApproved.delete(testCaseId);
    } else {
      newApproved.add(testCaseId);
      newRejected.delete(testCaseId);
    }

    setApprovedIds(newApproved);
    setRejectedIds(newRejected);

    if (newApproved.has(testCaseId)) {
      onApprove?.(testCaseId);
    }
  };

  const handleToggleRejection = (testCaseId) => {
    const newApproved = new Set(approvedIds);
    const newRejected = new Set(rejectedIds);

    if (newRejected.has(testCaseId)) {
      newRejected.delete(testCaseId);
    } else {
      newRejected.add(testCaseId);
      newApproved.delete(testCaseId);
    }

    setApprovedIds(newApproved);
    setRejectedIds(newRejected);

    if (newRejected.has(testCaseId)) {
      onReject?.(testCaseId);
    }
  };

  const handleSaveAll = async () => {
    if (!onConfirm) {
      toast.error("Save handler not available");
      return;
    }

    if (approvedIds.size === 0) {
      toast.error("Please approve at least one test case to save");
      return;
    }

    setIsSaving(true);
    try {
      // Filter test cases to only include approved ones
      const selectedTestCases = {};
      approvedIds.forEach((testCaseId) => {
        if (testCases[testCaseId]) {
          selectedTestCases[testCaseId] = testCases[testCaseId];
        }
      });

      const metadata = {
        sectionId: sectionId,
        reviewerId: reviewerId,
        projectId: projectId,
      };

      await onConfirm(selectedTestCases, metadata);
      toast.success(`Successfully saved ${approvedIds.size} test case(s)`);
      onClose();
    } catch (error) {
      console.error("Error saving test cases:", error);
      toast.error("Failed to save test cases. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-bg-primary rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-primary sticky top-0 bg-bg-primary z-10">
          <h2 className="text-xl font-semibold text-text-primary">
            Preview Test Cases ({Object.keys(testCases || {}).length})
          </h2>
          <button
            onClick={onClose}
            className="text-text-tertiary hover:text-text-secondary transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {testCases &&
            Object.entries(testCases).map(([testCaseId, testCase]) => (
              <div
                key={testCaseId}
                className="border border-border-primary rounded-lg p-4 bg-bg-secondary"
              >
                {editingId === testCaseId ? (
                  <EditTestCaseForm
                    testCaseId={testCaseId}
                    initialData={testCase}
                    onSave={(data) => handleSave(testCaseId, data)}
                    onCancel={handleCancelEdit}
                  />
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-text-primary mb-2">
                          {testCase["Test Scenario"] || "Untitled Test Case"}
                        </h3>
                        <div className="space-y-2 text-sm text-text-secondary">
                          {testCase["Precondition"] && (
                            <div>
                              <span className="font-medium">Precondition:</span>{" "}
                              {testCase["Precondition"]}
                            </div>
                          )}
                          {testCase["Test Steps"] && (
                            <div>
                              <span className="font-medium">Test Steps:</span>
                              <pre className="mt-1 whitespace-pre-wrap font-mono text-xs bg-bg-primary p-2 rounded">
                                {testCase["Test Steps"]}
                              </pre>
                            </div>
                          )}
                          {testCase["Expected Result"] && (
                            <div>
                              <span className="font-medium">
                                Expected Result:
                              </span>{" "}
                              {testCase["Expected Result"]}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleToggleApproval(testCaseId)}
                          className={`p-2 rounded transition-colors ${
                            approvedIds.has(testCaseId)
                              ? "bg-green-100 text-green-600"
                              : "bg-bg-hover text-text-secondary hover:text-text-primary"
                          }`}
                          title="Approve"
                        >
                          <CheckCircle2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleToggleRejection(testCaseId)}
                          className={`p-2 rounded transition-colors ${
                            rejectedIds.has(testCaseId)
                              ? "bg-red-100 text-red-600"
                              : "bg-bg-hover text-text-secondary hover:text-text-primary"
                          }`}
                          title="Reject"
                        >
                          <Circle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(testCaseId)}
                          className="p-2 rounded bg-bg-hover text-text-secondary hover:text-text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-text-tertiary">
                      {testCase["Priority"] && (
                        <span>
                          Priority:{" "}
                          <span className="font-medium">
                            {testCase["Priority"]}
                          </span>
                        </span>
                      )}
                      {testCase["Status"] && (
                        <span>
                          Status:{" "}
                          <span className="font-medium">
                            {testCase["Status"]}
                          </span>
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border-primary sticky bottom-0 bg-bg-primary">
          <div className="text-sm text-text-secondary">
            {approvedIds.size > 0 && (
              <span>
                {approvedIds.size} test case{approvedIds.size !== 1 ? "s" : ""}{" "}
                selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {onConfirm && (
              <Button
                onClick={handleSaveAll}
                disabled={isSaving || approvedIds.size === 0}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? "Saving..." : "Save AI Generate Test Cases"}
              </Button>
            )}
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCasesPreviewModal;
